import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import {
  inflate,
  PHONE_LAYOUT_QUERY,
  placePanel,
  SageTourStatus,
  SageTourStep,
  TourBox,
  TourPoint,
} from '../../../../shared/services/sage-tour.model';

type TourPhase = { kind: 'step'; index: number } | { kind: 'finish' } | { kind: 'hint' };

const SPOT_MARGIN = 6;
// Long enough for the cards to arrive after Help switches tab; after that the panel shows without a
// spotlight, rather than leaving a dimmed, empty page.
const FALLBACK_AFTER_MS = 1500;
const STOP_LOOKING_AFTER_MS = 10000;

/** The web and phone layouts are both in the DOM, so a target is the copy that is actually shown. */
function findVisible(slug: string): HTMLElement | null {
  const all = document.querySelectorAll<HTMLElement>(`[data-tour="${slug}"]`);
  return Array.from(all).find((el) => el.getClientRects().length > 0) ?? null;
}

/**
 * The first-time walkthrough over the Sage console (Mockup 03). Positions are tracked outside
 * Angular's zone, so scrolling refreshes only this component.
 */
@Component({
  selector: 'app-sage-tour',
  standalone: true,
  imports: [NgIf, NgFor, CdkTrapFocus],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './sage-tour.component.html',
  styleUrls: ['../onboarding-wizard/sage-wizard.shared.css', './sage-tour.component.css'],
})
export class SageTourComponent implements AfterViewInit, OnDestroy {
  @Input({ required: true }) steps: SageTourStep[] = [];
  /** Whether to point at Help once the tour ends; only the first time it ends. */
  @Input() offerHint = false;
  @Output() outcome = new EventEmitter<SageTourStatus>();
  @Output() closed = new EventEmitter<void>();

  phase: TourPhase = { kind: 'step', index: 0 };
  spot: TourBox | null = null;
  panelAt: TourPoint | null = null;
  glide = false;
  searching = true;

  @ViewChild('panel') private panel?: ElementRef<HTMLElement>;
  @ViewChild('primary') private primary?: ElementRef<HTMLButtonElement>;

  private target: HTMLElement | null = null;
  private frame: number | null = null;
  private searchStartedAt = 0;
  private focusPending = true;
  private destroyed = false;
  // zone.js doesn't patch ResizeObserver, so this already runs outside the zone.
  private readonly targetObserver = new ResizeObserver(() => this.schedule());
  private readonly onScroll = () => {
    this.glide = false;
    this.schedule();
  };
  private readonly onResize = () => this.schedule();
  // The dashboard panel scales in, and cards lift on hover, so a rect read mid-animation is off.
  private readonly onSettled = (event: Event) => {
    if (!this.host.nativeElement.contains(event.target as Node)) this.schedule();
  };

  constructor(
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>
  ) {}

  get index(): number {
    return this.phase.kind === 'step' ? this.phase.index : 0;
  }

  get step(): SageTourStep | undefined {
    return this.steps[this.index];
  }

  get isLast(): boolean {
    return this.index === this.steps.length - 1;
  }

  get announcement(): string {
    return this.phase.kind === 'step' && this.step ? `Step ${this.index + 1} of ${this.steps.length}: ${this.step.title}` : '';
  }

  private get slug(): string | null {
    if (this.phase.kind === 'step') return this.step?.slug ?? null;
    return this.phase.kind === 'hint' ? 'help' : null;
  }

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      document.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
      window.addEventListener('resize', this.onResize);
      document.addEventListener('animationend', this.onSettled, true);
      document.addEventListener('transitionend', this.onSettled, true);
    });
    this.startLooking();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    document.removeEventListener('scroll', this.onScroll, { capture: true });
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('animationend', this.onSettled, true);
    document.removeEventListener('transitionend', this.onSettled, true);
    if (this.frame !== null) cancelAnimationFrame(this.frame);
    this.targetObserver.disconnect();
    findVisible('help')?.focus({ preventScroll: true });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.phase.kind === 'step') this.skip();
    else if (this.phase.kind === 'finish') this.markDone();
    else this.gotIt();
  }

  next(): void {
    this.goTo(this.isLast ? { kind: 'finish' } : { kind: 'step', index: this.index + 1 });
  }

  back(): void {
    if (this.index > 0) this.goTo({ kind: 'step', index: this.index - 1 });
  }

  runAgain(): void {
    this.goTo({ kind: 'step', index: 0 });
  }

  skip(): void {
    this.end('skipped');
  }

  markDone(): void {
    this.end('done');
  }

  gotIt(): void {
    this.closed.emit();
  }

  isPhone(): boolean {
    return window.matchMedia?.(PHONE_LAYOUT_QUERY).matches ?? false;
  }

  private end(status: SageTourStatus): void {
    this.outcome.emit(status);
    if (this.offerHint) {
      this.goTo({ kind: 'hint' });
    } else {
      this.closed.emit();
    }
  }

  private goTo(phase: TourPhase): void {
    const fromStep = this.phase.kind === 'step';
    this.phase = phase;
    this.focusPending = true;
    this.glide = fromStep;
    // Between steps the tooltip stays where it is until the next card is measured; a new panel
    // stays hidden until it's placed, so it doesn't flash in the middle of the screen.
    this.searching = !(fromStep && phase.kind === 'step');
    this.startLooking();
  }

  private startLooking(): void {
    this.releaseTarget();
    this.searchStartedAt = performance.now();
    if (!this.slug) {
      this.spot = null;
      this.panelAt = null;
      this.searching = false;
      this.render();
      return;
    }
    this.render();
    this.zone.runOutsideAngular(() => this.look());
  }

  private readonly look = (): void => {
    this.frame = null;
    const slug = this.slug;
    if (this.destroyed || !slug) return;

    const found = findVisible(slug);
    if (found) {
      this.attach(found);
      return;
    }

    const waited = performance.now() - this.searchStartedAt;
    if (waited >= FALLBACK_AFTER_MS && (this.spot || this.searching)) {
      this.spot = null;
      this.panelAt = null;
      this.searching = false;
      this.render();
    }
    if (waited < STOP_LOOKING_AFTER_MS) this.frame = requestAnimationFrame(this.look);
  };

  private attach(el: HTMLElement): void {
    this.target = el;
    this.targetObserver.observe(el);
    const help = this.phase.kind === 'hint';
    el.scrollIntoView({
      // On a phone the tooltip is pinned to the bottom, so the card goes to the top; the phone view
      // makes room for that while the tour is on.
      block: !help && this.isPhone() ? 'start' : 'nearest',
      // A carousel card lines up with its snap point, or the row re-snaps and cuts it off; anything else,
      // such as a phone tab, moves only as far as it must.
      inline: getComputedStyle(el).scrollSnapAlign.includes('start') ? 'start' : 'nearest',
      // Bootstrap makes 'auto' smooth on the page, so reduced motion needs 'instant'.
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
    this.measure();
  }

  private releaseTarget(): void {
    if (this.frame !== null) cancelAnimationFrame(this.frame);
    this.frame = null;
    this.target = null;
    this.targetObserver.disconnect();
  }

  private schedule(): void {
    if (this.frame !== null || !this.target || this.destroyed) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.measure();
    });
  }

  private measure(): void {
    const el = this.target;
    if (!el) return;
    // Resized across the breakpoint, or the dashboard was left: find the copy that's shown now.
    if (!el.isConnected || el.getClientRects().length === 0) {
      this.releaseTarget();
      this.searchStartedAt = performance.now();
      this.look();
      return;
    }

    const rect = el.getBoundingClientRect();
    this.spot = inflate({ left: rect.left, top: rect.top, width: rect.width, height: rect.height }, SPOT_MARGIN);
    this.panelAt = this.placePanelFor(this.spot);
    this.searching = false;
    this.render();
  }

  private placePanelFor(spot: TourBox): TourPoint | null {
    const panel = this.panel?.nativeElement;
    // On a phone the step tooltip is pinned to the bottom by CSS.
    if (!panel || (this.phase.kind === 'step' && this.isPhone())) return null;
    return placePanel(
      spot,
      { width: panel.offsetWidth, height: panel.offsetHeight },
      { width: window.innerWidth, height: window.innerHeight },
      this.phase.kind === 'hint' ? 'end' : 'center'
    );
  }

  private render(): void {
    if (this.destroyed) return;
    this.cdr.detectChanges();
    if (this.focusPending && !this.searching && this.primary) {
      this.focusPending = false;
      this.primary.nativeElement.focus({ preventScroll: true });
    }
  }
}
